import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import {
  Input,
  InputChangeEvent,
  RadioButton,
  RadioButtonChangeEvent,
  Checkbox,
  CheckboxChangeEvent,
} from "@progress/kendo-react-inputs";
import { DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  ListItemProps,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import {
  DatePicker,
  DatePickerChangeEvent,
} from "@progress/kendo-react-dateinputs";
import useLocale from "../../../hooks/useLocale";
import useSwal from "../../../hooks/useSwal";
import {
  CreateNotificationRule,
  GetNotificationRule,
  NotificationTopic,
} from "../../../types/notification-setup/NotificationSetup";
import { TinyUser, User } from "../../../types/user";
import usersService from "../../../services/users.service";
import { Error } from "@progress/kendo-react-labels";
import { axiosWithAuth } from "../../../utils/customAxios";
import "./notification-setup.scss";
import { AxiosError } from "axios";
import useMasterData from "../../../hooks/useMasterData";
import { CallDirection, CallType, Sentiment } from "../../../types/master-data";
import { TinyObj } from "../../../types/TinyObj";
import { Topic } from "../../../types/topic";
import useAuth from "../../../hooks/useAuth";
import CustomMultiSelect from "../../../components/custom/form/CustomMultiSelect";
import useTranslation from "../../../hooks/useTranslation";
import { CustomCalendar } from "../../../components/custom/form/CustomCalendar";
import moment from "moment";
import userGroupsService from "../../../services/usergroups.service";
import { Group } from "../../../types/user-groups/Group";
import CustomUserList from "../../../components/custom/CustomUserList";

interface ErrorField {
  field: string;
  error: string;
}

interface UpsertNotificationSetupData {
  toggleDialog: () => void;
  upsertNotificationRule: (
    insert: boolean,
    notificationData: GetNotificationRule
  ) => void;
  notificationUpdateData?: GetNotificationRule;
  notificationRules?: GetNotificationRule[];
}

const UpsertNotificationSetup: React.FC<UpsertNotificationSetupData> = ({
  toggleDialog,
  upsertNotificationRule,
  notificationUpdateData,
  notificationRules,
}) => {
  const trans = useTranslation("UpsertNotificationSetup");
  const localeCtx = useLocale();
  const master = useMasterData();
  const swal = useSwal();
  const auth = useAuth();

  const [notificationName, setNotificationName] = useState<string>(
    notificationUpdateData ? notificationUpdateData.name : ""
  );
  const [topicsData, setTopicsData] = useState<TinyObj[]>([]);
  const [topics, setTopics] = useState<TinyObj[]>(
    notificationUpdateData ? notificationUpdateData.filterCriteria.topics : []
  );
  const [sentiments, setSentiments] = useState<Sentiment[]>(
    notificationUpdateData
      ? notificationUpdateData.filterCriteria.sentiments
      : []
  );
  // const [colorCategories, setColorCategories] = useState<ColorCategory[]>(
  //   notificationUpdateData
  //     ? notificationUpdateData.filterCriteria.colorCategories
  //     : []
  // );
  // const [evaluations, setEvaluations] = useState<NotificationEvaluation[]>(
  //   notificationUpdateData
  //     ? notificationUpdateData.filterCriteria.evaluations
  //     : []
  // );

  const [callDirection, setCallDirection] = useState<CallDirection[]>([]);
  const [callType, setCallType] = useState<CallType[]>([]);

  const [userEmailsData, setUserEmailsData] = useState<TinyUser[] | undefined>(
    undefined
  );
  const [userEmails, setUserEmails] = useState<TinyUser[]>(
    notificationUpdateData ? notificationUpdateData.filterCriteria.users : []
  );
  const [userGroupsData, setUserGroupsData] = useState<TinyObj[] | undefined>(
    undefined
  );

  const [myPlaybackNetworkNonUltimateUsers, setMyPlaybackNetworkNonUltimateUsers] = useState<TinyUser[]>([]);
  const [hasNonUltimateUserInRN, setHasNonUltimateUserInRN] =
    useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<TinyObj[]>(
    notificationUpdateData
      ? notificationUpdateData.filterCriteria.userGroups
      : []
  );
  const [expiryDate, setExpiryDate] = useState<Date | null>(
    notificationUpdateData
      ? !notificationUpdateData.expiryDate
        ? null
        : new Date(notificationUpdateData.expiryDate)
      : new Date()
  );
  const [radioButtonValue, setRadioButtonValue] = useState<string>(
    notificationUpdateData
      ? !notificationUpdateData.expiryDate
        ? "No"
        : "Yes"
      : "Yes"
  );

  const [allCallsChecked, setAllCallsChecked] = useState<boolean>(
    !notificationUpdateData
      ? true
      : !notificationUpdateData.filterCriteria.callDirection &&
          !notificationUpdateData.filterCriteria.callType &&
          notificationUpdateData.filterCriteria.userGroups.length === 0 &&
          notificationUpdateData.filterCriteria.users.length === 0
  );

  const [nonUltimateUsersFromUserEmails, setNonUltimateUsersFromUserEmail] = useState<TinyUser[]>([]);

  const [allUserGroups, setAllUserGroups] = useState<Group[]>([]);
  const [nonUltimateUsersInUserGroups, setNonUltimateUsersInUserGroups] = useState<User[]>([]);
  const [isUserGroupLicenseInValid, setIsUserGroupLicenseInValid] =
    useState<boolean>(false);

  const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
  const [loader, setLoader] = useState(false);
  const [stepper, setStepper] = useState<number>(0);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "UpsertNotificationSetup"
      ]
    ) {
      trans.fetchTranslations("UpsertNotificationSetup");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    setCallDirection(
      notificationUpdateData?.filterCriteria && master?.data
        ? !notificationUpdateData.filterCriteria.callDirection
          ? master?.data?.callDirections
          : master?.data?.callDirections.filter(
              (cd: CallDirection) =>
                cd.name ===
                notificationUpdateData.filterCriteria.callDirection?.name
            )
        : master?.data?.callDirections ?? []
    );

    setCallType(
      notificationUpdateData?.filterCriteria && master?.data
        ? !notificationUpdateData.filterCriteria.callType
          ? master?.data?.callTypes
          : master?.data?.callTypes.filter(
              (ct: CallType) =>
                ct.name === notificationUpdateData.filterCriteria.callType?.name
            )
        : master?.data?.callTypes ?? []
    );
  }, [master?.data]);

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(function checkUsersLicense() {
    if(master?.standardUsers && master?.standardUsers.length > 0 && userEmailsData){
      const filteredMyPlaybackNetworkNonUltimateUsers = userEmailsData.filter(record =>
        master?.standardUsers.some(user =>
          record.id === user.id &&
          (user.licensePackage == null || user.licensePackage?.baseLicense.name !== "Ultimate")
        )
      );
      setMyPlaybackNetworkNonUltimateUsers(filteredMyPlaybackNetworkNonUltimateUsers);

      const hasNonUltimateUser: boolean = userEmailsData.some(record =>
        master?.standardUsers.some(user =>
            record.id === user.id && (user.licensePackage === null || user.licensePackage?.baseLicense.name !== "Ultimate")
        )
      );
      setHasNonUltimateUserInRN(hasNonUltimateUser);
    }
  },[master?.standardUsers, userEmailsData])

  useEffect(()=>{
    if(userGroups){ //why we have written this useEffect.
      // todo probably call updateUserGroupsAndVerifyLicense here. 
      console.log(userGroups) // This tells us the selected users groups in the dropdown.
    }

  },[master?.standardUsers, userGroups])

    useEffect(()=>{
    if(notificationUpdateData){
      updateUserGroupsAndVerifyLicense(notificationUpdateData.filterCriteria.userGroups)
      onChangeUserEmails(notificationUpdateData.filterCriteria.users);
    }
  },[notificationUpdateData])

  const getAllData = async () => {
    try {
      const allTopics: TinyObj[] = [];
      const data = await usersService.getCurrentUser();
      data.topics?.forEach((topic: Topic) =>
        allTopics.push({
          id: topic.id,
          name: topic.topicName,
          isPublic: topic.isPublic,
        })
      );
      setTopicsData(allTopics);
      setUserGroupsData(data.associatedGroups?.playbackNetwork ?? []);
      setUserEmailsData(data.recordingNetwork.filter((user:TinyUser)=>!user.isArchived));
    } catch (error) {
      console.log(error);
      setTopicsData([]);
      setUserGroupsData([]);
      setUserEmailsData([]);
    }
  };

  const itemrenderEmails = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const itemChildren = (
      <span>
        <i className="bi bi-person m-r-6"></i>
        <span>{itemProps.dataItem.email}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const itemrenderGroups = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const itemChildren = (
      <span>
        <i className="bi bi-people m-r-6"></i>
        <span>{itemProps.dataItem.name}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const itemrenderTopics = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const itemChildren = (
      <span>
        <i
          className={`bi-${
            itemProps.dataItem.isPublic ? "people" : "lock-fill"
          } m-r-6`}
        ></i>
        <span>{itemProps.dataItem.name}</span>
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  };

  const handleChange = useCallback(
    (e: RadioButtonChangeEvent) => {
      if (e.value === "Yes") {
        setExpiryDate(new Date());
      } else {
        setExpiryDate(null);
      }
      setRadioButtonValue(e.value);
    },
    [radioButtonValue]
  );

  const onChangeUserEmails = (value: TinyUser[]) => {
    const eventIds = new Set(value.map((obj: { id: number }) => obj.id));
    const matchedData = master?.standardUsers.filter((obj) =>
      eventIds.has(obj.id)
    );
    const nonUltimateUsersMasterData = matchedData?.filter(
      (obj) =>
        !obj.licensePackage ||
        obj.licensePackage.baseLicense.name !== "Ultimate"
    );

    const nonUltimateUsers =
      value.length > 0 &&
      nonUltimateUsersMasterData &&
      nonUltimateUsersMasterData?.length > 0
        ? value.filter((obj: { id: number }) =>
            nonUltimateUsersMasterData.find((obj2) => obj2.id === obj.id)
          )
        : [];

    setNonUltimateUsersFromUserEmail(nonUltimateUsers);
    setUserEmails([...value]);
  };

  const onChangeUserGroups = (event: MultiSelectChangeEvent) => {
    updateUserGroupsAndVerifyLicense(event.value);
  };

  const updateUserGroupsAndVerifyLicense = async (
    value: TinyObj[]
  ) => {
    if (value.length === 0) {
      setAllUserGroups([]);
      setIsUserGroupLicenseInValid(false);
      setNonUltimateUsersInUserGroups([]);
    } else {
      if (allUserGroups.length <= value.length) {
        // A new userGroup has been selected in the multiselect. We'll never have the == case.

        // Find which id from value does not exist in allUserGroups.
        // Call the API for that id and push response to allUserGroups
        // Verify license from masterData

        let tempAllUserGroups = [...allUserGroups];
        const tempAllUserGroupIds = new Set(
          tempAllUserGroups.map((group) => group.id)
        );
        const missingIds = value
          .filter((group: { id: number }) => !tempAllUserGroupIds.has(group.id))
          .map((group: { id: number }) => group.id);
        if (missingIds?.length > 0) {
          const newCompleteUserGroup = await userGroupsService.getUserGroupById(
            missingIds[0]
          );
          tempAllUserGroups.push(newCompleteUserGroup);
        }

        verifyUserGroupLicense(tempAllUserGroups);
        setAllUserGroups(tempAllUserGroups);
      } else {
        // An existing userGroup has been removed in the multiselect

        // Find which id from allUserGroups does not exist in value
        // Remove that id from allUserGroups
        // Verify license from masterData

        let tempAllUserGroups = [...allUserGroups];
        const allEventIds = new Set(
          value.map((obj: { id: number }) => obj.id)
        );
        const userGroupRemoved = tempAllUserGroups.filter(
          (obj) => !allEventIds.has(obj.id)
        );
        if (userGroupRemoved?.length > 0) {
          tempAllUserGroups = tempAllUserGroups.filter(
            (obj) => obj.id !== userGroupRemoved[0].id
          );
        }

        verifyUserGroupLicense(tempAllUserGroups);
        setAllUserGroups(tempAllUserGroups);
      }
    }
    setUserGroups([...value]);
  };

  const verifyUserGroupLicense = (tempAllUserGroups: Group[]) => {
    const _idsToCheck = new Set<number>();
    tempAllUserGroups.forEach((group) => {
      group.groupMembers.forEach((member) =>{
        if(!member.member.isArchived) _idsToCheck.add(member.member.id)
      });
    });

    const matchedData = master?.standardUsers.filter((obj) =>
      _idsToCheck.has(obj.id)
    );
    const nonUltimateUsersMasterData = matchedData?.filter(
      (obj) =>
        !obj.licensePackage ||
        obj.licensePackage.baseLicense.name !== "Ultimate"
    );
    if (nonUltimateUsersMasterData && nonUltimateUsersMasterData.length > 0) {
      setNonUltimateUsersInUserGroups(nonUltimateUsersMasterData);
      setIsUserGroupLicenseInValid(true);
    } else {
      setNonUltimateUsersInUserGroups([]);
      setIsUserGroupLicenseInValid(false);
    }
  };

  const onChangeNotificationName = (event: InputChangeEvent) => {
    if (event.value.length > 100) {
      setErrorFields([
        ...errorFields,
        {
          field: "notificationName",
          error: trans.fetchLabelKeyTranslation(
            "FieldError1",
            "Notification Name cannot be more than 100 characters"
          ),
        },
      ]);
    } else {
      setErrorFields([
        ...errorFields.filter(
          (error: ErrorField) => error.field !== "notificationName"
        ),
      ]);
    }
    setNotificationName(event.value);
  };

  const onChangeTopics = (event: MultiSelectChangeEvent) => {
    setTopics([...event.value]);
  };

  const onChangeSentiments = (type?: Sentiment) => {
    const findSentiment: Sentiment | undefined = sentiments?.find(
      (s: Sentiment) => s.name.toLowerCase() === type?.name.toLowerCase()
    );
    if (type) {
      if (!findSentiment) {
        setSentiments([...sentiments, type]);
      } else {
        setSentiments(
          sentiments.filter(
            (s: Sentiment) => s.name.toLowerCase() !== type?.name.toLowerCase()
          )
        );
      }
    }
  };

  // const onChangeFlags = (type?: ColorCategory) => {
  //   const findColor: ColorCategory | undefined = colorCategories?.find(
  //     (color: ColorCategory) =>
  //       color.name.toLowerCase() === type?.name.toLowerCase()
  //   );
  //   if (type) {
  //     if (!findColor) {
  //       setColorCategories([...colorCategories, type]);
  //     } else {
  //       setColorCategories(
  //         colorCategories.filter(
  //           (color: ColorCategory) =>
  //             color.name.toLowerCase() !== type.name.toLowerCase()
  //         )
  //       );
  //     }
  //   }
  // };

  // const onChangeEvaluation = (type: NotificationEvaluation) => {
  //   const findEval: NotificationEvaluation | undefined = evaluations?.find(
  //     (e: NotificationEvaluation) =>
  //       e.name.toLowerCase() === type?.name.toLowerCase()
  //   );
  //   if (!findEval) {
  //     setEvaluations([...evaluations, type]);
  //   } else {
  //     setEvaluations(
  //       evaluations.filter(
  //         (evaluation: NotificationEvaluation) =>
  //           evaluation.name.toLowerCase() !== type.name.toLowerCase()
  //       )
  //     );
  //   }
  // };

  const onChangeExpiryDate = (event: DatePickerChangeEvent) => {
    if (event.value && event.value > new Date()) {
      setExpiryDate(event.value);
    }
  };

  const allCallsCheckedHandler = (event: CheckboxChangeEvent) => {
    if (event.value) {
      if (master?.data) setCallDirection(master?.data?.callDirections);
      if (master?.data) setCallType(master.data.callTypes);
      setUserGroups([]);
      setUserEmails([]);
    }
    setAllCallsChecked(!allCallsChecked);
  };

  const callTypeChangeHandler = (type: string, event: CheckboxChangeEvent) => {
    const findCallType = master?.data?.callTypes.find(
      (ct: CallType) => ct.name == type
    );
    if (event.value) {
      if (findCallType) setCallType([...callType, findCallType]);
    } else {
      const updatedCallTypes = callType.filter(
        (ct: CallType) => ct.name !== type
      );
      setCallType(updatedCallTypes);
    }
  };

  const callDirectionChangeHandler = (
    direction: string,
    event: CheckboxChangeEvent
  ) => {
    const findCallDirection = master?.data?.callDirections.find(
      (cd: CallDirection) => cd.name == direction
    );
    if (event.value) {
      if (findCallDirection)
        setCallDirection([...callDirection, findCallDirection]);
    } else {
      const updatedCallDirection = callDirection.filter(
        (cd: CallDirection) => cd.name !== direction
      );
      setCallDirection(updatedCallDirection);
    }
  };

  const findName = (notification: GetNotificationRule) => {
    if (notificationUpdateData) {
      return (
        notification.name === notificationName.trim() &&
        notification.name !== notificationUpdateData.name &&
        notification.id !== notificationUpdateData.id
      );
    } else {
      return notification.name === notificationName.trim();
    }
  };

  const nameChecker = async () => {
    if (notificationRules?.find(findName) !== undefined) {
      const swalOptions: any = {
        customClass: {
          container: "my-swal",
        },
        title: `${trans.fetchLabelKeyTranslation(
          "InvalidNameTitle",
          "Name Already Exists"
        )}`,
        text: `${trans.fetchLabelKeyTranslation(
          "InvalidNameText",
          "Notification Rule with same name already exists."
        )}`,
        icon: "error",
        showCancelButton: false,
        confirmButtonColor: "#3085d6",
        confirmButtonText: trans.fetchLabelKeyTranslation(
          "SwalConfirmText",
          "Ok"
        ),
      };
      await swal.fire(swalOptions);
    } else {
      setStepper(1);
    }
  };

  const stepperHandler = (stepperTrack: number) => {
    setStepper(stepperTrack);
  };

  const submitHandler = async () => {
    if (notificationName.trim() === "") {
      setErrorFields([
        ...errorFields,
        {
          field: "notificationName",
          error: trans.fetchLabelKeyTranslation(
            "FieldError2",
            "Notification Name is required"
          ),
        },
      ]);
    } else {
      const Updatedtopics: NotificationTopic[] = [];
      topics.map((topic: TinyObj) =>
        Updatedtopics.push({
          name: topic.name,
          isPublic: topic.isPublic ?? false,
        })
      );

      let convertedExpiryDate: string | null = null;
      if (expiryDate) {
        convertedExpiryDate = moment(expiryDate)
          .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      const data: CreateNotificationRule = {
        name: notificationName.trim(),
        topics: Updatedtopics,
        sentiments: sentiments.map((sentiment: Sentiment) => {
          return sentiment.name;
        }),
        // colorCategories: colorCategories.map((color: ColorCategory) => {
        //   return color.name;
        // }),
        // evaluations: evaluations.map((evaluation: NotificationEvaluation) => {
        //   return evaluation.name;
        // }),
        callDirection:
          allCallsChecked ||
          callDirection.length === master?.data?.callDirections.length
            ? undefined
            : callDirection[0].name,
        callType:
          allCallsChecked || callType.length === master?.data?.callTypes.length
            ? undefined
            : callType[0].name,
        userEmails: userEmails.map((email: TinyUser) => {
          return email.email;
        }),
        userGroups: userGroups.map((group: TinyObj) => {
          return group.name;
        }),
        expiryDate: convertedExpiryDate,
      };
      setLoader(true);
      try {
        const response = notificationUpdateData
          ? await axiosWithAuth.put(
              `/NotificationRule/${notificationUpdateData.id}`,
              data
            )
          : await axiosWithAuth.post("/NotificationRule", data);
        const swalOptions: any = {
          customClass: {
            container: "my-swal",
          },
          title: `${
            notificationUpdateData
              ? trans.fetchLabelKeyTranslation(
                  "SwalUpdateTitle",
                  `Notification Rule updated`
                )
              : trans.fetchLabelKeyTranslation(
                  "SwalCreateTitle",
                  "Notification Rule created"
                )
          }`,
          text: `${
            notificationUpdateData
              ? trans.fetchLabelKeyTranslation(
                  "SwalUpdateText",
                  `Your notification rule has been updated`
                )
              : trans.fetchLabelKeyTranslation(
                  "SwalCreateText",
                  `Your notification rule has been created`
                )
          }`,
          icon: "success",
          showCancelButton: false,
          confirmButtonColor: "#3085d6",
          confirmButtonText: trans.fetchLabelKeyTranslation(
            "SwalConfirmText",
            "Ok"
          ),
        };
        await swal.fire(swalOptions);
        upsertNotificationRule(
          notificationUpdateData ? false : true,
          response.data
        );
        toggleDialog();
        setLoader(false);
      } catch (error) {
        if (error instanceof AxiosError) {
          const swalOptions: any = {
            customClass: {
              container: "my-swal",
            },
            title: `${
              notificationUpdateData
                ? trans.fetchLabelKeyTranslation(
                    "SwalUpdateTitleFailed",
                    "Notification Rule updation failed"
                  )
                : trans.fetchLabelKeyTranslation(
                    "SwalCreateTitleFailed",
                    "Notification Rule creation failed"
                  )
            }`,
            text: `${
              notificationUpdateData
                ? trans.fetchLabelKeyTranslation(
                    "SwalUpdateTextFailed",
                    "Notification Rule cannot be updated. Please try again later."
                  )
                : trans.fetchLabelKeyTranslation(
                    "SwalCreateTextFailed",
                    "Notification Rule cannot be created. Please try again later."
                  )
            }`,
            icon: "error",
            showCancelButton: false,
            confirmButtonColor: "#3085d6",
            confirmButtonText: trans.fetchLabelKeyTranslation(
              "SwalConfirmText",
              "Ok"
            ),
          };
          await swal.fire(swalOptions);
        }
        setLoader(false);
      }
    }
  };

  return (
    <>
      <div className="addNotificationForm py-3">
        {stepper === 0 && (
          <div className="row h-100">
            <div className="col-12 h-100">
              <div className="d-flex align-items-center h-100 px-4 py-2">
                <div>
                  <h3 className=" mb-3 font-weight-bold">
                    {trans.fetchLabelKeyTranslation(
                      "StayInformedText",
                      "Stay informed with notifications"
                    )}
                  </h3>
                  <div className="fs-14 mb-4">
                    {/* {trans.fetchLabelKeyTranslation(
                  "UpsertNotificationSetupTitle",
                  "Notification Name"
                )} */}
                    {trans.fetchLabelKeyTranslation(
                      "NotificationNameLabelDesc",
                      "Automatically receive email alerts when calls meet your defined criteria—like poor sentiment or successful sales outcomes—so you never miss a critical moment."
                    )}
                  </div>
                  <div className="noteFormCol col-7 mx-auto text-center">
                    <label className="font-weight-bold">
                      {trans.fetchLabelKeyTranslation(
                        "NotificationNameText",
                        "Notification Name"
                      )}
                    </label>
                    <Input
                      className="noteFormColIput fs-13 text-center"
                      placeholder={trans.fetchLabelKeyTranslation(
                        "NotificationNamePlaceholder",
                        "Enter notification name"
                      )}
                      name="search"
                      value={notificationName}
                      id="search"
                      type="text"
                      onChange={onChangeNotificationName}
                    />
                    <Error>
                      {
                        errorFields.find(
                          (error: ErrorField) =>
                            error.field === "notificationName"
                        )?.error
                      }
                    </Error>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {stepper > 0 && (
          <div className="row h-100">
            <div className="col-md-8 col-sm-12">
              {stepper === 1 && (
                <div className="pl-4 pb-2">
                  <div className="row">
                    <div className="col-sm-12 mb-2">
                      <div className="fs-13 font-weight-semi">
                        {trans.fetchLabelKeyTranslation(
                          "NotificationScopeKey",
                          "Select Scope"
                        )}
                      </div>
                      <div className="fs-11 text-muted">
                        {trans.fetchLabelKeyTranslation(
                          "NotificationScopeDecription",
                          "Select recorded calls in my playback network or just recorded calls for selected users."
                        )}
                      </div>
                    </div>
                    <div className="col-sm-12 mb-2">
                      <div className="labYesNo p-b-10 p-t-10">
                        <span className="p-r-20">
                          <RadioButton
                            id="AllCall"
                            name="AllCall"
                            value="AllCall"
                            onChange={() => {
                              setAllCallsChecked(true);
                              setUserEmails([]);
                              setUserGroups([]);
                              setAllUserGroups([]);
                              setNonUltimateUsersInUserGroups([]);
                              setNonUltimateUsersFromUserEmail([])
                            }}
                            checked={allCallsChecked === true}
                          />
                          <label
                            htmlFor={"AllCall"}
                            className={
                              "k-checkbox-label fs-13 ml-1 d-inline-block"
                            }
                            style={{ lineHeight: "1" }}
                          >
                            {trans.fetchLabelKeyTranslation(
                              "MonitorMyPlaybackKey",
                              "Monitor My Playback Network"
                            )}
                          </label>
                        </span>
                        <span>
                          <RadioButton
                            id="SelectedCall"
                            name="AllCall"
                            value="SelectedCall"
                            onChange={() => {
                              setAllCallsChecked(false);
                            }}
                            checked={allCallsChecked === false}
                          />
                          <label
                            htmlFor={"SelectedCall"}
                            className={
                              "k-checkbox-label fs-13 ml-1 d-inline-block"
                            }
                            style={{ lineHeight: "1" }}
                          >
                            {trans.fetchLabelKeyTranslation(
                              "MonitorSelectedUsersKey",
                              "Monitor Selected Users"
                            )}
                          </label>
                        </span>
                      </div>
                      {/* {hasNonUltimateUserInRN && allCallsChecked && (
                        <span style={{ fontSize: "10px", color: "red" }}>
                          <i className="bi bi-exclamation-circle-fill"></i>{" "}
                          &nbsp;
                          {trans.fetchLabelKeyTranslation(
                            "NonUltimateUserInRNText",
                            "Some users in My Playback Network require an Ultimate licence to enable notification alerts."
                          )}
                          
                          &nbsp;
                        </span>
                      )} */}
                      {myPlaybackNetworkNonUltimateUsers.length > 0 && allCallsChecked && (
                        <span style={{ fontSize: "10px", color: "red" }}>
                          <i className="bi bi-exclamation-circle-fill"></i>{" "}
                          &nbsp;
                          {trans.fetchLabelKeyTranslation(
                            "NonUltimateUserInRNText",
                            "Some users in My Playback Network require an Ultimate licence to enable notification alerts."
                          )}
                           <CustomUserList 
                            userList={myPlaybackNetworkNonUltimateUsers ? myPlaybackNetworkNonUltimateUsers : []} 
                            maxDisplay={5} 
                            title={'Users without Ultimate license'} 
                            fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
                          />
                          &nbsp;
                        </span>
                      )}
                    </div>
                  </div>
                  {/* <div className="row">
              <div className="col-sm-12 col-md-6">
                <div className="labInputBox">
                  <div className="labInputLbl">
                    &nbsp;
                    {trans.fetchLabelKeyTranslation(
                      "CallTypeFieldTitle",
                      "Call Type"
                    )}
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="labInputBox d-flex">
                        <Checkbox
                          checked={
                            callType.find(
                              (ct: CallType) => ct.name === "External"
                            )
                              ? true
                              : false
                          }
                          id="callTypeExternal"
                          name="allCalls"
                          className="m-l-4 fs-11"
                          onChange={(event: CheckboxChangeEvent) =>
                            callTypeChangeHandler("External", event)
                          }
                          disabled={allCallsChecked}
                        />
                        <label
                          htmlFor={"callTypeExternal"}
                          className={
                            "k-checkbox-label fs-13 ml-1 d-inline-block text-muted"
                          }
                        >
                          {master?.data?.callTypes.find(
                            (ct: CallType) => ct.name === "External"
                          )?.localizationValue ?? "External"}
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="labInputBox d-flex">
                        <Checkbox
                          checked={
                            callType.find(
                              (ct: CallType) => ct.name === "Internal"
                            )
                              ? true
                              : false
                          }
                          id="callTypeInternal"
                          name="allCalls"
                          className="m-l-4 fs-11"
                          onChange={(event: CheckboxChangeEvent) =>
                            callTypeChangeHandler("Internal", event)
                          }
                          disabled={allCallsChecked}
                        />
                        <label
                          htmlFor={"callTypeInternal"}
                          className={
                            "k-checkbox-label fs-13 ml-1 d-inline-block text-muted"
                          }
                        >
                          {master?.data?.callTypes.find(
                            (ct: CallType) => ct.name === "Internal"
                          )?.localizationValue ?? "Internal"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-12 col-md-6">
                <div className="labInputBox">
                  <div className="labInputLbl">
                    {trans.fetchLabelKeyTranslation(
                      "CallDirectionFieldTitle",
                      "Call Direction"
                    )}
                  </div>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="labInputBox d-flex">
                        <Checkbox
                          id="callDirectionDialled"
                          checked={
                            callDirection.find(
                              (cd: CallDirection) => cd.name === "Dialled"
                            )
                              ? true
                              : false
                          }
                          name="allCalls"
                          className="m-l-4 fs-11"
                          onChange={(event: CheckboxChangeEvent) =>
                            callDirectionChangeHandler("Dialled", event)
                          }
                          disabled={allCallsChecked}
                        />
                        <label
                          htmlFor={"callTypeDialled"}
                          className={
                            "k-checkbox-label fs-13 ml-1 d-inline-block text-muted"
                          }
                        >
                          {master?.data?.callDirections.find(
                            (cd: CallDirection) => cd.name === "Dialled"
                          )?.localizationValue ?? "Dialled"}
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="labInputBox d-flex">
                        <Checkbox
                          id="callDirectionReceived"
                          checked={
                            callDirection.find(
                              (cd: CallDirection) => cd.name === "Received"
                            )
                              ? true
                              : false
                          }
                          name="allCalls"
                          className="m-l-4 fs-11"
                          onChange={(event: CheckboxChangeEvent) =>
                            callDirectionChangeHandler("Received", event)
                          }
                          disabled={allCallsChecked}
                        />
                        <label
                          htmlFor={"callDirectionReceived"}
                          className={
                            "k-checkbox-label fs-13 ml-1 d-inline-block text-muted"
                          }
                        >
                          {master?.data?.callDirections.find(
                            (cd: CallDirection) => cd.name === "Received"
                          )?.localizationValue ?? "Received"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

                  {!allCallsChecked && (
                    <div>
                      <div className="labInputBox">
                        {!allCallsChecked &&
                          userEmails.length === 0 &&
                          userGroups.length === 0 && (
                            <span style={{ fontSize: "10px", color: "red" }}>
                              <i className="bi bi-exclamation-circle-fill"></i>{" "}
                              &nbsp;
                              {trans.fetchLabelKeyTranslation(
                                "UserOrGroupSelectionDropdownWarning",
                                "Please select one or more users/user groups"
                              )}
                              &nbsp;
                            </span>
                          )}
                        <div className="labInputLbl">
                          {trans.fetchLabelKeyTranslation(
                            "UserFieldTitle",
                            "User"
                          )}
                        </div>
                        <div className="labInput">
                          <CustomMultiSelect
                            disabled={allCallsChecked}
                            data={userEmailsData?.filter(
                              (u) => u.isArchived === false
                            )}
                            onChange={(event:MultiSelectChangeEvent)=>onChangeUserEmails(event.value)}
                            value={userEmails}
                            placeholder={trans.fetchLabelKeyTranslation(
                              "UserFieldPlaceholder",
                              "Please Select..."
                            )}
                            itemRender={itemrenderEmails}
                            dataItemKey="email"
                            textField="email"
                            filtering={true}
                            sorting={true}
                            sortField={"email"}
                          />
                        </div>
                        {nonUltimateUsersFromUserEmails && nonUltimateUsersFromUserEmails.length > 0 && (
                          <div className="m-b-40">
                            <div className="fs-10 text-danger mb-1">
                               <span>{trans.fetchLabelKeyTranslation("UsersRequireUltimate","The following users require an Ultimate licence to enable alerts.")} </span>
                               {nonUltimateUsersFromUserEmails.length > 5 && (<span>{trans.fetchLabelKeyTranslation("MoreThanFiveRequireUltimate", "Click below to list and search users that need an Ultimate licence to enable notification alerts.")}</span>)}
                            </div>
                            <CustomUserList 
                              userList={nonUltimateUsersFromUserEmails ? nonUltimateUsersFromUserEmails : []} 
                              maxDisplay={5} 
                              title={'Users without Ultimate license'} 
                              fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
                              />
                          </div>
                        )}
                      </div>
                      <div className="labInputBox">
                        <div className="labInputLbl">
                          {trans.fetchLabelKeyTranslation(
                            "UserGroupFieldTitle",
                            "User Group"
                          )}
                        </div>
                        <div className="labInput">
                          <CustomMultiSelect
                            disabled={allCallsChecked}
                            data={userGroupsData}
                            onChange={onChangeUserGroups}
                            value={userGroups}
                            placeholder={trans.fetchLabelKeyTranslation(
                              "UserGroupFieldPlaceholder",
                              "Please Select..."
                            )}
                            itemRender={itemrenderGroups}
                            dataItemKey="name"
                            textField="name"
                            filtering={true}
                            sorting={true}
                            sortField={"name"}
                          />
                        </div>
                        {
                          nonUltimateUsersInUserGroups && nonUltimateUsersInUserGroups.length > 0 && 
                          (
                            <div>
                              <div className="fs-10 text-danger mb-1">
                                {trans.fetchLabelKeyTranslation("UsersGroupRequireUltimate",
                                  "Some Users in the user group(s) selected require an Ultimate license to enable notification alerts.")}
                                
                              </div>
                              <CustomUserList 
                                userList={nonUltimateUsersInUserGroups ? nonUltimateUsersInUserGroups : []} 
                                maxDisplay={5} 
                                title={'Users without Ultimate license'} 
                                fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
                                />
                            </div>
                          )
                        }

                        {/* {isUserGroupLicenseInValid && (
                          <div>
                            <div className="fs-10 text-danger mb-2">
                              {trans.fetchLabelKeyTranslation(
                                "NonUltimateUserGroupWarning",
                                "Some Users in this group require an Ultimate license to enable notification alerts."
                              )}
                            </div>
                          </div>
                        )} */}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {stepper === 2 && (
                <div className="pl-4 pb-2">
                  <div className="row">
                    <div className="col-md-12 col-sm-12 mb-2">
                      <div className="fs-13 font-weight-semi">
                        {trans.fetchLabelKeyTranslation(
                          "SelectTriggersKey",
                          "Select Triggers"
                        )}
                      </div>
                      <div className="fs-11 text-muted">
                        {trans.fetchLabelKeyTranslation(
                          "NotificationCriteriaTitleDesc",
                          "Select Topics and/or Sentiment ratings to trigger email notifications"
                        )}
                      </div>
                      {sentiments.length === 0 && topics.length === 0 && (
                        <span style={{ fontSize: "10px", color: "red" }}>
                          <i className="bi bi-exclamation-circle-fill"></i>{" "}
                          &nbsp;
                          {trans.fetchLabelKeyTranslation(
                            "NotificationCriteriawarning",
                            "Please select atleast one or more criteria for notification"
                          )}
                          &nbsp;
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-sm-12">
                      {auth?.checkTopicAvailibility() && (
                        <div className="labInputBox">
                          <div className="labInputLbl">
                            &nbsp;
                            {trans.fetchLabelKeyTranslation(
                              "NotificationTopicsTitle",
                              "Select Topics"
                            )}
                          </div>
                          <div className="labInput">
                            <CustomMultiSelect
                              data={topicsData}
                              onChange={onChangeTopics}
                              value={topics}
                              placeholder="Please select ..."
                              itemRender={itemrenderTopics}
                              dataItemKey="name"
                              textField="name"
                              filtering={true}
                              sorting={true}
                              sortField={"name"}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <div className="chipBox d-flex align-items-center">
                        <div className="chipBoxLbl">
                          &nbsp;
                          {trans.fetchLabelKeyTranslation(
                            "NotificationSentimentsTitle",
                            "Select Sentiments"
                          )}
                        </div>
                        <div className="chipBoxBtn ml-2">
                          <ButtonGroup>
                            <Button
                              selected={
                                !sentiments.find(
                                  (sentiment: Sentiment) =>
                                    sentiment.name.toLowerCase() === "positive"
                                )
                                  ? false
                                  : true
                              }
                              onClick={() =>
                                onChangeSentiments(
                                  master?.data?.sentiments.find(
                                    (s: Sentiment) =>
                                      s.name.toLowerCase() === "positive"
                                  )
                                )
                              }
                              togglable={true}
                            >
                              <span>
                                <i className="bi bi-emoji-smile-fill text-success"></i>
                              </span>
                            </Button>
                            <Button
                              selected={
                                !sentiments.find(
                                  (sentiment: Sentiment) =>
                                    sentiment.name.toLowerCase() === "neutral"
                                )
                                  ? false
                                  : true
                              }
                              onClick={() =>
                                onChangeSentiments(
                                  master?.data?.sentiments.find(
                                    (s: Sentiment) =>
                                      s.name.toLowerCase() === "neutral"
                                  )
                                )
                              }
                              togglable={true}
                            >
                              <span>
                                <i className="bi bi-emoji-neutral-fill text-warning"></i>
                              </span>
                            </Button>
                            <Button
                              selected={
                                !sentiments.find(
                                  (sentiment: Sentiment) =>
                                    sentiment.name.toLowerCase() === "negative"
                                )
                                  ? false
                                  : true
                              }
                              onClick={() =>
                                onChangeSentiments(
                                  master?.data?.sentiments.find(
                                    (s: Sentiment) =>
                                      s.name.toLowerCase() === "negative"
                                  )
                                )
                              }
                              togglable={true}
                            >
                              <span>
                                <i className="bi bi-emoji-frown-fill text-danger"></i>
                              </span>
                            </Button>
                          </ButtonGroup>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {stepper === 3 && (
                <div className="pl-4 pb-2">
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="fs-13 font-weight-semi">
                        {/* {trans.fetchLabelKeyTranslation(
                          "NotificationExpiryHeading",
                          "Notification Duration"
                        )} */}
                      </div>
                      <div className="fs-11 text-muted">
                        {trans.fetchLabelKeyTranslation(
                          "SelectExpiryHeading",
                          "Select Expiry"
                        )}
                      </div>
                    </div>
                    <div className="col-sm-12">
                      <div className="labYesNo p-b-20 p-t-10">
                        <span className="p-r-20">
                          <div className="labInputLbl">
                            &nbsp;
                            {trans.fetchLabelKeyTranslation(
                              "NotificationExpirySubHeading",
                              "Do you want to set an expiry date?"
                            )}
                          </div>
                          <RadioButton
                            name="group1"
                            value="Yes"
                            checked={radioButtonValue === "Yes"}
                            label={trans.fetchLabelKeyTranslation(
                              "NotificationExpiryRadioButtonLabel1",
                              "Yes"
                            )}
                            onChange={handleChange}
                          />
                        </span>
                        <span>
                          <RadioButton
                            name="group1"
                            value="No"
                            checked={radioButtonValue === "No"}
                            label={trans.fetchLabelKeyTranslation(
                              "NotificationExpiryRadioButtonLabel2",
                              "No"
                            )}
                            onChange={handleChange}
                          />
                        </span>
                      </div>
                      {radioButtonValue === "Yes" && (
                        <div className="row">
                          <div className="col-sm-9 col-md-6">
                            <div className="labInputBox">
                              <div className="labInputLbl">
                                {trans.fetchLabelKeyTranslation(
                                  "NotificationExpiryTitle",
                                  "Set Notification Expiry date"
                                )}
                              </div>
                              <div className="labInput">
                                <DatePicker
                                  min={new Date()}
                                  value={expiryDate}
                                  onChange={onChangeExpiryDate}
                                  calendar={CustomCalendar}
                                  format="dd/MM/yyyy"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-4 col-sm-12 border-left text-break">
              <div className="fs-13 pb-2">
                {trans.fetchLabelKeyTranslation(
                  "NotificationNameKey",
                  "Notification Name"
                )}
                :<span className="mx-1"></span>
                <span className="text-primary">{notificationName}</span>
              </div>
              <div className="fs-13 font-weight-semi">
                {trans.fetchLabelKeyTranslation(
                  "LookForAllCallsTxt",
                  "Monitor recorded calls for"
                )}
              </div>
              <div className="labSubHeading"></div>

              <div className="row mb-2">
                <div className="col">
                  <div className="labInputLbl">
                    {trans.fetchLabelKeyTranslation("UserFieldTitle", "User")}:
                    <span className="mx-1"></span>
                    <span className="text-primary">
                      {allCallsChecked
                        ? trans.fetchLabelKeyTranslation("AllTxt", "All")
                        : userEmails.map((user: TinyUser, index: number) => {
                            if (index === 0) return user.email;
                            else return `, ${user.email}`;
                          })}
                    </span>
                    {/* {((allCallsChecked) || (userGroups.length > 0)) &&
                  <div style={{ color: "red", textAlign: "center" }}>{trans.fetchLabelKeyTranslation("AndTxt","and")}</div>} */}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col">
                  <div className="labInputLbl">
                    {trans.fetchLabelKeyTranslation(
                      "UserGroupFieldTitle",
                      "User Group"
                    )}
                    :<span className="mx-1"></span>
                    <span className="text-primary">
                      {allCallsChecked
                        ? trans.fetchLabelKeyTranslation("AllTxt", "All")
                        : userGroups.map((group: TinyObj, index: number) => {
                            if (index === 0) return group.name;
                            else return `, ${group.name}`;
                          })}
                    </span>
                  </div>
                </div>
              </div>

              {(topics.length > 0 || sentiments.length > 0) && (
                <div className="row mb-2">
                  <div className="col border-top border-top-dashed">
                    <div className="fs-13 font-weight-semi mt-2">
                      {trans.fetchLabelKeyTranslation(
                        "NotificationSentTxt",
                        "Send notification when"
                      )}
                    </div>
                  </div>
                </div>
              )}

              {topics.length > 0 && (
                <div className="row mb-2">
                  <div className="col">
                    <div className="labInputLbl">
                      {trans.fetchLabelKeyTranslation(
                        "TopicsDetectedTxt",
                        "If any of the following Topics are detected:"
                      )}
                      <br />
                      <span>
                        {trans.fetchLabelKeyTranslation(
                          "TopicsHeading",
                          "Topics"
                        )}{" "}
                        ={" "}
                      </span>
                      <span className="text-primary ">
                        {topics.map((topic: TinyObj, index: number) => {
                          if (index === 0) return topic.name;
                          else return `, ${topic.name}`;
                        })}
                      </span>
                      {/* {(sentiments.length > 0) &&
                    <div style={{ color: "red", textAlign: "center" }}>{trans.fetchLabelKeyTranslation("AndTxt","and")}</div>} */}
                    </div>
                  </div>
                </div>
              )}
              {topics.length > 0 && sentiments.length > 0 && (
                <span className=" fw-bold">
                  {trans.fetchLabelKeyTranslation("AndTxtCaps", "AND")}
                </span>
              )}
              {sentiments.length > 0 && (
                <div className="row mb-2">
                  <div className="col">
                    <div className="labInputLbl">
                      {trans.fetchLabelKeyTranslation(
                        "OverallSentimentTxt",
                        "if overall call sentiment is:"
                      )}
                      <br />
                      <span>
                        {trans.fetchLabelKeyTranslation(
                          "SentimentRatingHeading",
                          "Sentiment Rating"
                        )}{" "}
                        ={" "}
                      </span>
                      <span className="text-primary">
                        {sentiments.map(
                          (sentiment: Sentiment, index: number) => {
                            if (sentiment.name.toLowerCase() === "positive") {
                              return (
                                <i
                                  key={0}
                                  className="bi bi-emoji-smile-fill fs-18 text-success"
                                >
                                  &nbsp;
                                </i>
                              );
                            } else if (
                              sentiment.name.toLowerCase() === "negative"
                            ) {
                              return (
                                <i
                                  key={1}
                                  className="bi bi-emoji-frown-fill fs-18 text-danger"
                                >
                                  &nbsp;
                                </i>
                              );
                            } else {
                              return (
                                <i
                                  key={2}
                                  className="bi bi-emoji-neutral-fill fs-18 text-warning"
                                >
                                  &nbsp;
                                </i>
                              );
                            }
                          }
                        )}
                      </span>
                      {/* {(colorCategories.length > 0 || evaluations.length > 0) &&
                    <div style={{ color: "red", textAlign: "center" }}>or</div>} */}
                    </div>
                  </div>
                </div>
              )}

              {/* {colorCategories.length > 0 && 
            <div className="row mb-2">
              <div className="col">
                <div className="labInputLbl">
                  {trans.fetchLabelKeyTranslation(
                    "FlagFieldTitle",
                    "Flag"
                  )}:
                  <span className="mx-1"></span>
                  <span className="text-primary">
                    {colorCategories.map((color: ColorCategory) => {
                      return <span
                        className="flagRed"
                        style={{
                          color: color.hexCode,
                        }}
                      >
                        <i className="bi bi-flag-fill">&nbsp;</i>
                      </span>
                    })}
                  </span>
                  
                  {(evaluations.length > 0) &&
                    <div style={{ color: "red", textAlign: "center" }}>or</div>}
                </div>
              </div>
            </div>
            } */}

              {/* {evaluations.length > 0 && <div className="row mb-2">
              <div className="col">
                <div className="labInputLbl">
                  {trans.fetchLabelKeyTranslation(
                    "EvaluationFieldTitle",
                    "Evaluation"
                  )}:
                  <span className="mx-1"></span>
                  <span className="text-primary">
                    {evaluations.map((evaluation: NotificationEvaluation, index: number) => {
                      if (evaluation.name.toLowerCase() === "good") {
                        if (index === 0) return trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption1",
                          "Good"
                        );
                        else return `, ${trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption1",
                          "Good"
                        )}`
                      } else if (evaluation.name.toLowerCase() === "average") {
                        if (index === 0) return trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption2",
                          "Average"
                        );
                        else return `, ${trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption2",
                          "Average"
                        )}`
                      } else if (evaluation.name.toLowerCase() === "poor") {
                        if (index === 0) return trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption3",
                          "Poor"
                        );
                        else return `, ${trans.fetchLabelKeyTranslation(
                          "EvaluationFieldOption3",
                          "Poor"
                        )}`
                      } 
                    })}
                  </span>
                </div>
              </div>
            </div>} */}
            </div>
          </div>
        )}
      </div>
      <DialogActionsBar>
        {stepper === 0 && (
          <button
            className={`k-button k-button-md k-button-solid k-button-solid-base k-rounded-md btn bg-primary text-white
            ${
              notificationName === undefined ||
              notificationName === null ||
              notificationName?.trim() === ""
                ? "k-disabled disabledBtn"
                : ""
            }`}
            onClick={nameChecker}
            disabled={
              notificationName === undefined ||
              notificationName === null ||
              notificationName?.trim() === ""
            }
          >
            {trans.fetchLabelKeyTranslation("NextBtnTxt", "Next")}
          </button>
        )}

        {stepper === 1 && (
          <>
            <button
              className={`k-button k-button-md k-button-solid k-button-solid-base k-rounded-md btn bg-primary text-white`}
              onClick={() => stepperHandler(0)}
            >
              {trans.fetchLabelKeyTranslation("PreviousBtnTxt", "Previous")}
            </button>
            <button
              className={`k-button k-button-md k-button-solid k-button-solid-base k-rounded-md btn bg-primary text-white
            ${
              !allCallsChecked &&
              userEmails.length === 0 &&
              userGroups.length === 0
                ? "k-disabled disabledBtn"
                : ""
            }`}
              onClick={() => stepperHandler(2)}
              disabled={
                !allCallsChecked &&
                userEmails.length === 0 &&
                userGroups.length === 0
              }
            >
              {trans.fetchLabelKeyTranslation("NextBtnTxt", "Next")}
            </button>
          </>
        )}

        {stepper === 2 && (
          <>
            <button
              className={`k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-primary text-white`}
              onClick={() => stepperHandler(1)}
            >
              {trans.fetchLabelKeyTranslation("PreviousBtnTxt", "Previous")}
            </button>
            <button
              className={`k-button k-button-md k-button-solid k-button-solid-base k-rounded-md btn bg-primary text-white
            ${
              !allCallsChecked &&
              (callType.length == 0 || callDirection.length == 0)
                ? "k-disabled disabledBtn"
                : ""
            }`}
              onClick={() => stepperHandler(3)}
              disabled={sentiments.length === 0 && topics.length === 0}
            >
              {trans.fetchLabelKeyTranslation("NextBtnTxt", "Next")}
            </button>
          </>
        )}

        {stepper === 3 && (
          // <>
          //   <button
          //     className={`k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-primary text-white`}
          //     onClick={() => stepperHandler(-1)}
          //   >
          //     {trans.fetchLabelKeyTranslation("PreviousBtnTxt", "Previous")}
          //   </button>

          //   <button
          //     className={`k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-primary
          //   ${topics.length === 0 && sentiments.length === 0 ? "text-white-7" : "text-white"}`}
          //     onClick={() => stepperHandler(1)}
          //     disabled={topics.length === 0 && sentiments.length === 0}
          //   >
          //     {trans.fetchLabelKeyTranslation("NextBtnTxt", "Next")}
          //   </button>
          // </>
          <>
            <button
              className={`k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-primary text-white`}
              onClick={() => stepperHandler(2)}
            >
              {trans.fetchLabelKeyTranslation("PreviousBtnTxt", "Previous")}
            </button>

            <button
              className={`k-button k-button-md k-button-solid k-button-solid-base k-rounded-md btn bg-primary text-white
            ${
              errorFields.length > 0 || loader ? "k-disabled disabledBtn" : ""
            }`}
              onClick={submitHandler}
              disabled={errorFields.length > 0 || loader}
            >
              {trans.fetchLabelKeyTranslation("SubmitBtnTxt", "Submit")}
            </button>
          </>
        )}
      </DialogActionsBar>
    </>
  );
};

export default UpsertNotificationSetup;
